/**
 * useGuidedDemo - Guided demo mode hook (Narrative Shell).
 *
 * After playback reaches Ready and the user is idle for 5 seconds, the hook
 * auto-starts a guided demo: it seeks to the turn with the lowest
 * overall_score, then 3 seconds later drops to 0.5× speed and plays —
 * drawing the viewer's attention to the weakest turn. Any playback-related
 * keypress (Space / ArrowLeft / ArrowRight / Comma / Period) dismisses
 * the demo.
 *
 * 当回放进入 Ready 且用户空闲 5 秒后，本 hook 自动启动引导演示：seek 到
 * 综合评分最低的弯，3 秒后切到 0.5× 倍速并播放，引导观众关注最差的弯。
 * 任意回放相关按键（Space / ArrowLeft / ArrowRight / Comma / Period）即取消演示。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Turn } from '../../types/ski-data';
import { PlaybackState } from '../../types/enums';

// ----- Types -----

/** Guided demo lifecycle state. / 引导演示生命周期状态。 */
export type GuidedDemoState = 'idle' | 'demoing' | 'dismissed';

/** Public API returned by {@link useGuidedDemo}. / useGuidedDemo 返回的公开 API。 */
export interface GuidedDemoApi {
  /** Current lifecycle state. / 当前生命周期状态。 */
  state: GuidedDemoState;
  /** Dismiss the demo (idle/demoing → dismissed). / 取消演示（idle/demoing → dismissed）。 */
  dismiss: () => void;
  /** Reset back to idle (re-armable). / 重置回 idle（可重新触发）。 */
  reset: () => void;
}

/** Input params for {@link useGuidedDemo}. / useGuidedDemo 的输入参数。 */
export interface UseGuidedDemoParams {
  playbackState: PlaybackState;
  turns: Turn[];
  seek: (ts: number) => void;
  play: () => void;
  setSpeed: (s: 0.5 | 1.0) => void;
}

// ----- Constants -----

/** Idle delay before the demo auto-starts (ms). / 演示自动启动前的空闲时长（毫秒）。 */
const IDLE_DELAY_MS = 5000;

/** Delay between seek and slow-speed play (ms). / seek 到慢速播放之间的延迟（毫秒）。 */
const DEMO_DELAY_MS = 3000;

/** Keys that dismiss the demo when pressed. / 按下即取消演示的按键集合。 */
const DISMISS_KEYS = new Set([' ', 'ArrowLeft', 'ArrowRight', ',', '.']);

// ----- Hook -----

export function useGuidedDemo(params: UseGuidedDemoParams): GuidedDemoApi {
  const { playbackState, turns, seek, play, setSpeed } = params;
  const [state, setState] = useState<GuidedDemoState>('idle');

  // Timer IDs stored in refs so they can be cleared on cleanup / dismiss.
  // 定时器 ID 存于 ref，便于在清理 / 取消时清除。
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const demoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs mirroring the latest params so effect closures read fresh values
  // without re-subscribing on every identity change.
  // 镜像最新参数的 ref，使 effect 闭包读取最新值而无需因引用变化重新订阅。
  const turnsRef = useRef(turns);
  const seekRef = useRef(seek);
  const playRef = useRef(play);
  const setSpeedRef = useRef(setSpeed);
  turnsRef.current = turns;
  seekRef.current = seek;
  playRef.current = play;
  setSpeedRef.current = setSpeed;

  // Clear both timers (idempotent). / 清除两个定时器（幂等）。
  const clearTimers = useCallback(() => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (demoTimerRef.current != null) {
      clearTimeout(demoTimerRef.current);
      demoTimerRef.current = null;
    }
  }, []);

  // dismiss: idle/demoing → dismissed, clear all timers.
  // dismiss：idle/demoing → dismissed，清除全部定时器。
  const dismiss = useCallback(() => {
    setState((prev) => (prev === 'dismissed' ? prev : 'dismissed'));
    clearTimers();
  }, [clearTimers]);

  // reset: return to idle (re-armable). / reset：回到 idle（可重新触发）。
  const reset = useCallback(() => {
    clearTimers();
    setState('idle');
  }, [clearTimers]);

  // idle → demoing: when playback becomes Ready, wait 5s then transition.
  // Clear the timer on cleanup if playback leaves Ready before it fires.
  // idle → demoing：回放变为 Ready 后等待 5 秒切换；若期间离开 Ready 则清理定时器。
  useEffect(() => {
    if (state !== 'idle') return;
    if (playbackState !== PlaybackState.Ready) return;
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      setState('demoing');
    }, IDLE_DELAY_MS);
    return () => {
      if (idleTimerRef.current != null) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [state, playbackState]);

  // demoing: seek to the lowest-score turn, then 3s later set 0.5× and play.
  // demoing：seek 到最低分弯，3 秒后切 0.5× 并播放。
  useEffect(() => {
    if (state !== 'demoing') return;
    const currentTurns = turnsRef.current;
    if (currentTurns.length === 0) return;
    // Find the turn with the lowest overall_score.
    // 找到 overall_score 最低的弯。
    const lowest = currentTurns.reduce((min, t) =>
      t.overall_score < min.overall_score ? t : min
    );
    seekRef.current(lowest.start_ms);
    demoTimerRef.current = setTimeout(() => {
      demoTimerRef.current = null;
      setSpeedRef.current(0.5);
      playRef.current();
    }, DEMO_DELAY_MS);
    return () => {
      if (demoTimerRef.current != null) {
        clearTimeout(demoTimerRef.current);
        demoTimerRef.current = null;
      }
    };
  }, [state]);

  // Auto-dismiss on user keyboard interaction. / 用户按键交互即自动取消。
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (DISMISS_KEYS.has(e.key)) {
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [dismiss]);

  return { state, dismiss, reset };
}
